/**
 * SenditSell feature flags — PRD v1.1.2 + mock UI plan
 * Complex features default OFF; enable via VITE_FEATURE_FLAGS or DevFlagPanel.
 */

export type FeatureFlag =
  | 'mdm.vendors'
  | 'mdm.customers'
  | 'mdm.categories'
  | 'mdm.productTaxonomy'
  | 'mdm.products'
  | 'mdm.preferredVendor'
  | 'governance.core'
  | 'governance.rbacV2'
  | 'sales.orders'
  | 'sales.claims'
  | 'procurement.po'
  | 'procurement.poFromSo'
  | 'occ.board'
  | 'shipping.batch'
  | 'shipping.notice'
  | 'fulfillment.receive'
  | 'finance.ap'
  | 'finance.ar'
  | 'finance.glStatements'
  | 'engines.pricing'
  | 'engines.costing'
  | 'reports.opsPack'
  | 'reorder.suggestions'
  | 'agents.layer';

export const FEATURE_FLAG_META: Record<
  FeatureFlag,
  { label: string; wave: 0 | 1 | 2 | 3; complex: boolean }
> = {
  'mdm.vendors': { label: 'Vendor master', wave: 0, complex: false },
  'mdm.customers': { label: 'Customer master', wave: 0, complex: false },
  'mdm.categories': { label: 'Party categories', wave: 0, complex: false },
  'mdm.productTaxonomy': { label: 'Product taxonomy (Class→Group→Sub)', wave: 1, complex: true },
  'mdm.products': { label: 'Product CRUD', wave: 1, complex: true },
  'mdm.preferredVendor': { label: 'Preferred vendor on product', wave: 1, complex: true },
  'governance.core': {
    label: 'Organisation, roles, users, audit and data quality',
    wave: 0,
    complex: true,
  },
  // S4.2 flag contract — `governance.rbacV2` ships dark (DEFAULT OFF below).
  //   OFF: the resolver runs its degenerate path (flat role-permission union,
  //        no inheritsFrom closure, no Axis-B org scoping) — byte-identical to
  //        pre-rbacV2 behaviour. The only always-on change is the
  //        ProtectedRoute NPE fix (crash-safe on BOTH flag paths).
  //   ON:  Axis-B row filtering in the SO/PO/OCC/shipping mocks + the Wave-2/3
  //        admin surfaces (org tree editor, 角色階層 hierarchy tab + level
  //        manager, inherited matrix cells, scoped assignment, effective
  //        preview, impact diff, why-403 explainer) + the System-module nav
  //        deep links (navigation.constants.ts, also requires governance.core).
  // The pure resolver in src/lib/rbac/ is always-on, zero-I/O, and consumed
  // verbatim by MSW and the UI guard (shared-selector parity test).
  // Full contract: docs/rbac.md
  'governance.rbacV2': {
    label: 'Inheritable RBAC (org hierarchy + role inheritance)',
    wave: 0,
    complex: true,
  },
  'sales.orders': { label: 'Sales orders', wave: 2, complex: true },
  'sales.claims': { label: 'Sales claims', wave: 2, complex: true },
  'procurement.po': { label: 'Purchase orders + vendor select', wave: 2, complex: true },
  'procurement.poFromSo': { label: 'SO → PO', wave: 2, complex: true },
  'occ.board': { label: 'Order Control Center', wave: 2, complex: true },
  'shipping.batch': { label: 'Shipment batch', wave: 2, complex: true },
  'shipping.notice': { label: 'Shipping notice', wave: 2, complex: true },
  'fulfillment.receive': { label: 'Goods receipt', wave: 2, complex: true },
  'finance.ap': { label: 'AP / dual-control', wave: 3, complex: true },
  'finance.ar': { label: 'AR', wave: 3, complex: true },
  'finance.glStatements': { label: 'P&L / Balance sheet', wave: 3, complex: true },
  'engines.pricing': { label: 'Pricing engine', wave: 3, complex: true },
  'engines.costing': { label: 'Costing engine', wave: 3, complex: true },
  'reports.opsPack': { label: 'Ops reports pack', wave: 3, complex: true },
  'reorder.suggestions': { label: 'Reorder suggestions review queue', wave: 3, complex: true },
  'agents.layer': { label: 'Agent layer', wave: 3, complex: true },
};

export const DEFAULT_FLAGS: Record<FeatureFlag, boolean> = {
  'mdm.vendors': true,
  'mdm.customers': true,
  'mdm.categories': true,
  'mdm.productTaxonomy': false,
  'mdm.products': false,
  'mdm.preferredVendor': false,
  'governance.core': false,
  'governance.rbacV2': false, // FINAL default OFF (S4.2) — flips only by owner decision
  'sales.orders': false,
  'sales.claims': false,
  'procurement.po': false,
  'procurement.poFromSo': false,
  'occ.board': false,
  'shipping.batch': false,
  'shipping.notice': false,
  'fulfillment.receive': false,
  'finance.ap': false,
  'finance.ar': false,
  'finance.glStatements': false,
  'engines.pricing': false,
  'engines.costing': false,
  'reports.opsPack': false,
  'reorder.suggestions': false,
  'agents.layer': false,
};

/** Demo profile: closed-loop masters + SO/PO/OCC (engines/finance still off) */
export const DEMO_CLOSED_LOOP_FLAGS: FeatureFlag[] = [
  'governance.core',
  'mdm.productTaxonomy',
  'mdm.products',
  'mdm.preferredVendor',
  'sales.orders',
  'procurement.po',
  'procurement.poFromSo',
  'occ.board',
  'shipping.notice',
  'shipping.batch',
  'fulfillment.receive',
];

const STORAGE_KEY = 'senditsell.featureFlags';

/**
 * Local override channels (the localStorage overlay written by the DevFlagPanel
 * and the `?ff=` URL params) are honoured ONLY in the dev server and in
 * mock-mode (demo) builds. A real-API build (no VITE_USE_MSW, or
 * `--mode production`) ignores both — otherwise a browser that once visited a
 * demo build on the same origin could carry a `senditsell.featureFlags` key
 * that silently flips hidden modules in the real app.
 */
function overridesAllowed(): boolean {
  const mockBuild =
    import.meta.env.VITE_USE_MSW === 'true' && import.meta.env.MODE !== 'production';
  return Boolean(import.meta.env.DEV) || mockBuild;
}

function parseEnvFlags(): Partial<Record<FeatureFlag, boolean>> {
  const out: Partial<Record<FeatureFlag, boolean>> = {};
  const json = import.meta.env.VITE_FEATURE_FLAGS_JSON as string | undefined;
  if (json) {
    try {
      const parsed = JSON.parse(json) as Record<string, boolean>;
      (Object.keys(DEFAULT_FLAGS) as FeatureFlag[]).forEach((k) => {
        if (typeof parsed[k] === 'boolean') out[k] = parsed[k];
      });
    } catch {
      console.warn('[flags] Invalid VITE_FEATURE_FLAGS_JSON');
    }
  }
  const csv = import.meta.env.VITE_FEATURE_FLAGS as string | undefined;
  if (csv) {
    csv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((k) => {
        if (k in DEFAULT_FLAGS) out[k as FeatureFlag] = true;
      });
  }
  return out;
}

function readStorage(): Partial<Record<FeatureFlag, boolean>> {
  if (!overridesAllowed()) {
    // Real-API build: the overlay is ignored AND actively scrubbed, so a
    // demo-tainted origin self-heals on the first real-app boot.
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable — nothing to scrub */
    }
    return {};
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Partial<Record<FeatureFlag, boolean>>;
  } catch {
    return {};
  }
}

export function writeFlagStorage(flags: Partial<Record<FeatureFlag, boolean>>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
}

export function clearFlagStorage() {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Dev / mock-build URL override: `?ff=reorder.suggestions,occ.board` enables the named
 * flags, `?ff=all` enables every flag, `?ff=none` disables every flag, and a `-` prefix
 * turns one off (`?ff=all,-finance.ap`). Highest precedence and ignored in real-API
 * builds, so a shareable demo link always lands on the right features without touching
 * the console.
 */
function parseUrlFlags(): Partial<Record<FeatureFlag, boolean>> {
  // Allowed in the dev server AND in mock-mode (demo) builds — a demo artifact contains
  // no real data, so URL toggles are harmless there. A real-API build (no VITE_USE_MSW,
  // or --mode production) still ignores ?ff= entirely (same gate as the localStorage
  // overlay — see overridesAllowed()).
  if (!overridesAllowed() || typeof window === 'undefined') return {};
  try {
    const raw = new URLSearchParams(window.location.search).get('ff');
    if (!raw) return {};
    const out: Partial<Record<FeatureFlag, boolean>> = {};
    const keys = Object.keys(DEFAULT_FLAGS) as FeatureFlag[];
    const token = raw.trim().toLowerCase();
    if (token === 'all') return Object.fromEntries(keys.map((k) => [k, true])) as typeof out;
    if (token === 'none') return Object.fromEntries(keys.map((k) => [k, false])) as typeof out;
    raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((entry) => {
        const off = entry.startsWith('-');
        const key = off ? entry.slice(1) : entry;
        if (isFeatureFlag(key)) out[key] = !off;
      });
    return out;
  } catch {
    return {};
  }
}

/**
 * Resolve effective flags: defaults < env < localStorage < URL `?ff=` (dev overrides win).
 * The localStorage overlay and `?ff=` apply ONLY in dev or mock (demo) builds — in a
 * real-API build both channels are ignored and the storage key is removed, so the
 * baked env/default flags are the whole truth.
 */
export function resolveFeatureFlags(
  storageOverlay?: Partial<Record<FeatureFlag, boolean>>
): Record<FeatureFlag, boolean> {
  const env = parseEnvFlags();
  const storage = storageOverlay ?? (typeof localStorage !== 'undefined' ? readStorage() : {});
  const url = parseUrlFlags();
  const resolved = { ...DEFAULT_FLAGS };
  (Object.keys(DEFAULT_FLAGS) as FeatureFlag[]).forEach((k) => {
    if (typeof env[k] === 'boolean') resolved[k] = env[k]!;
    if (typeof storage[k] === 'boolean') resolved[k] = storage[k]!;
    if (typeof url[k] === 'boolean') resolved[k] = url[k]!;
  });
  // Persist a URL override into the same store the DevFlagPanel uses so the link's
  // effect sticks across client-side navigation + reloads. Dev-only; `?ff=none` resets.
  if (import.meta.env.DEV && Object.keys(url).length > 0 && typeof localStorage !== 'undefined') {
    try {
      writeFlagStorage({ ...storage, ...url });
    } catch {
      /* storage unavailable — in-memory resolution above still applies */
    }
  }
  return resolved;
}

export function isFeatureFlag(key: string): key is FeatureFlag {
  return key in DEFAULT_FLAGS;
}

/**
 * V3-S6 — product-image drag & drop + the mock image overlay are demo-honest:
 * no real file storage exists yet (next-sprint ticket), so only the dev server
 * and mock (demo) builds show the upload affordance or touch the overlay key.
 * Same static gate as the override channels above and pricingStore's
 * `demoPersistenceAllowed()`; a real-API build ships URL entry only. NOT a
 * feature flag on purpose — product surfaces already ride `mdm.products`, and
 * this is a build-seam property, not a toggleable capability.
 */
export function imageUploadDemoAllowed(): boolean {
  return overridesAllowed();
}
